import { useEffect, useRef, useState } from "react";
import { Maximize2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

/**
 * PopoutTextarea — inline `<textarea>` with an ⤢ button that opens the
 * same value in a large focused modal. Solves the "typing long paragraphs
 * in a cramped 3-line box" complaint without breaking the compact editor
 * layout.
 *
 * Behaviour:
 *   • Small mode: normal inline textarea, user can type as usual.
 *   • Popout: full-screen-friendly modal with a 20-row textarea, save on
 *     close (or Cmd/Ctrl-Enter, or the SAVE button) and toast "opgeslagen".
 *
 * Props:
 *   value          — string, current text
 *   onChange       — (next: string) => void, called on every keystroke
 *   placeholder    — placeholder text
 *   title          — modal heading (defaults to field label if provided)
 *   rows           — inline rows (default 4)
 *   className      — extra classes on the inline textarea
 *   dataTestId     — base test-id; suffixes appended for sub-elements
 *   minPopoutChars — hide the expand button until value length ≥ this
 *                    (0 to always show; default 0)
 */
export default function PopoutTextarea({
  value = "",
  onChange,
  placeholder = "",
  title = "",
  rows = 4,
  className = "",
  dataTestId = "popout-textarea",
  minPopoutChars = 0,
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const initialRef = useRef(value);
  const modalRef = useRef(null);

  // Sync draft when parent value changes AND the modal is closed. While the
  // modal is open we intentionally hold on to the user's live edits.
  useEffect(() => {
    if (!open) setDraft(value);
  }, [value, open]);

  const openModal = () => {
    initialRef.current = value;
    setDraft(value);
    setOpen(true);
  };

  const closeAndSave = () => {
    // Only fire toast when there was an actual change — otherwise every
    // open/close cycle would spam the user with a false-positive save.
    if (draft !== initialRef.current) {
      onChange?.(draft);
      toast.success(t("popoutSaved"));
    }
    setOpen(false);
  };

  const closeWithoutSave = () => {
    setDraft(initialRef.current);
    setOpen(false);
  };

  // Cmd/Ctrl+Enter → save & close.
  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      closeAndSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeWithoutSave();
    }
  };

  const showExpand = minPopoutChars <= 0 || (value?.length || 0) >= minPopoutChars;

  return (
    <div className="relative">
      <textarea
        data-testid={dataTestId}
        className={`ed-input ${className}`}
        placeholder={placeholder}
        value={value}
        rows={rows}
        onChange={(e) => onChange?.(e.target.value)}
      />
      {showExpand && (
        <button
          type="button"
          data-testid={`${dataTestId}-expand`}
          onClick={openModal}
          title={t("popoutExpand")}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center border border-neutral-300 bg-white/95 hover:border-neutral-900 hover:bg-white transition-colors text-neutral-600 hover:text-neutral-900"
        >
          <Maximize2 size={12} strokeWidth={1.7} />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-neutral-900/60 flex items-center justify-center p-4 sm:p-8"
          data-testid={`${dataTestId}-modal`}
          onClick={(e) => { if (e.target === modalRef.current) closeAndSave(); }}
          ref={modalRef}
        >
          <div
            className="bg-[#F9F8F6] w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl"
            onKeyDown={onKey}
          >
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-neutral-200">
              <p className="ed-eyebrow" data-testid={`${dataTestId}-modal-title`}>
                {title || t("popoutTitle")}
              </p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 hidden sm:inline">
                  {t("popoutShortcut")}
                </span>
                <button
                  type="button"
                  data-testid={`${dataTestId}-modal-close`}
                  onClick={closeWithoutSave}
                  className="w-8 h-8 flex items-center justify-center hover:bg-neutral-200"
                  aria-label={t("popoutCancel")}
                >
                  <X size={16} strokeWidth={1.6} />
                </button>
              </div>
            </div>
            <textarea
              autoFocus
              data-testid={`${dataTestId}-modal-textarea`}
              className="ed-input flex-1 !border-0 !bg-transparent !rounded-none min-h-[20rem] font-serif text-base leading-relaxed p-5 focus:outline-none resize-none"
              placeholder={placeholder}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-neutral-200">
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500" data-testid={`${dataTestId}-modal-chars`}>
                {t("popoutChars", { n: draft.length })}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`${dataTestId}-modal-cancel`}
                  onClick={closeWithoutSave}
                  className="ed-btn ed-btn-outline"
                >
                  {t("popoutCancel")}
                </button>
                <button
                  type="button"
                  data-testid={`${dataTestId}-modal-save`}
                  onClick={closeAndSave}
                  className="ed-btn inline-flex items-center gap-2"
                >
                  <Check size={13} strokeWidth={1.7} /> {t("popoutSave")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
